import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import { useSidebar } from '../../contexts/SidebarContext';
import Icon from '../../components/AppIcon';
import { supplierService } from '../../services/supplierService';
import { advancedBillingService } from '../../services/advancedBillingService';
import TerritoryMapSection from './components/TerritoryMapSection';
import SupplierAssignmentPanel from './components/SupplierAssignmentPanel';
import MarketCoverageAnalysis from './components/MarketCoverageAnalysis';
import CapacityPlanningSection from './components/CapacityPlanningSection';

const MarketSupplierConfiguration = () => {
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('territory');
  const [suppliers, setSuppliers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [territoryData, setTerritoryData] = useState([]);
  const [capacityData, setCapacityData] = useState([]);
  const [filters, setFilters] = useState({
    countryId: 'all',
    roleType: 'all',
    status: 'all'
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [suppliersData, countriesData, territoryNetworkData, capacityAnalysisData] = await Promise.all([
        supplierService?.getAllSuppliers(filters),
        advancedBillingService?.getAllCountries(),
        supplierService?.getSupplierNetworkByTerritory(),
        supplierService?.getCapacityAnalysisByMarket()
      ]);
      
      setSuppliers(suppliersData || []);
      setCountries(countriesData || []);
      setTerritoryData(territoryNetworkData || []);
      setCapacityData(capacityAnalysisData || []);
    } catch (error) {
      console.error('Error loading market supplier data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const tabs = [
    { id: 'territory', label: 'Carte des Territoires', icon: 'Map' },
    { id: 'assignment', label: 'Affectation Fournisseurs', icon: 'Users' },
    { id: 'coverage', label: 'Couverture Marchés', icon: 'Globe' },
    { id: 'capacity', label: 'Planification Capacité', icon: 'TrendingUp' }
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Configuration Fournisseurs & Marchés</h1>
                <p className="text-gray-600 mt-1">Gestion du réseau de fournisseurs par territoire et optimisation de la couverture géographique</p>
              </div>
              <button
                onClick={() => navigate('/supplier-management-dashboard')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Icon name="Settings" className="w-5 h-5" />
                Gestion Fournisseurs
              </button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
                <select
                  value={filters?.countryId}
                  onChange={(e) => handleFilterChange('countryId', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tous les pays</option>
                  {countries?.map(country => (
                    <option key={country?.id} value={country?.id}>
                      {country?.countryName} ({country?.countryCode})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de Fournisseur</label>
                <select
                  value={filters?.roleType}
                  onChange={(e) => handleFilterChange('roleType', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tous les types</option>
                  <option value="production_supplier">Fournisseur Production</option>
                  <option value="dentist_supplier">Dentiste Fournisseur</option>
                </select>
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={filters?.status}
                  onChange={(e) => handleFilterChange('status', e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {tabs?.map(tab => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab?.id
                        ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon name={tab?.icon} className="w-5 h-5" />
                    {tab?.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {activeTab === 'territory' && (
                <TerritoryMapSection 
                  territoryData={territoryData}
                  suppliers={suppliers}
                  countries={countries}
                  onRefresh={loadData}
                />
              )}
              
              {activeTab === 'assignment' && (
                <SupplierAssignmentPanel 
                  suppliers={suppliers}
                  countries={countries}
                  onRefresh={loadData}
                />
              )}
              
              {activeTab === 'coverage' && (
                <MarketCoverageAnalysis 
                  territoryData={territoryData}
                  capacityData={capacityData}
                  countries={countries}
                />
              )}
              
              {activeTab === 'capacity' && (
                <CapacityPlanningSection 
                  capacityData={capacityData}
                  suppliers={suppliers}
                  countries={countries}
                />
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default MarketSupplierConfiguration;